import React from 'react'
import { Link } from 'react-router-dom'
import { cn } from '../../utils'

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonBaseProps {
  variant?: ButtonVariant
  size?: ButtonSize
  className?: string
  children: React.ReactNode
  disabled?: boolean
  loading?: boolean
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
}

interface ButtonAsButton extends ButtonBaseProps {
  as?: 'button'
  href?: never
  to?: never
  type?: 'button' | 'submit' | 'reset'
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
}

interface ButtonAsLink extends ButtonBaseProps {
  as: 'link'
  to: string
  href?: never
  type?: never
  onClick?: () => void
}

interface ButtonAsAnchor extends ButtonBaseProps {
  as: 'a'
  href: string
  to?: never
  type?: never
  onClick?: () => void
  target?: string
  rel?: string
}

type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor

const variantStyles: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-primary-400 text-white',
    'hover:bg-primary-500',
    'focus-visible:ring-primary-400/50',
    'shadow-sm hover:shadow-md',
    'active:scale-[0.98]',
    // Ensure visibility in both modes
    'dark:bg-primary-400 dark:text-white dark:hover:bg-primary-300'
  ),
  secondary: cn(
    'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100',
    'hover:bg-gray-200 dark:hover:bg-gray-700',
    'focus-visible:ring-gray-400/50',
    'active:scale-[0.98]'
  ),
  ghost: cn(
    'bg-transparent text-gray-700 dark:text-gray-300',
    'hover:bg-gray-100 dark:hover:bg-gray-800',
    'focus-visible:ring-gray-400/50',
    'active:scale-[0.98]'
  ),
  outline: cn(
    'bg-transparent',
    'text-gray-900 dark:text-white',
    'border-2 border-gray-300 dark:border-gray-600',
    'hover:border-primary-400 hover:text-primary-500 hover:bg-primary-50',
    'dark:hover:border-primary-400 dark:hover:text-primary-300 dark:hover:bg-primary-900/20',
    'focus-visible:ring-primary-400/50',
    'active:scale-[0.98]'
  ),
}

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-sm gap-1.5',
  md: 'px-6 py-2.5 text-base gap-2',
  lg: 'px-8 py-3 text-lg gap-2.5',
}

const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(({
  variant = 'primary',
  size = 'md',
  className,
  children,
  disabled = false,
  loading = false,
  icon,
  iconPosition = 'right',
  ...props
}, ref) => {
  const baseStyles = cn(
    // Base
    'inline-flex items-center justify-center',
    'font-medium rounded-lg',
    'transition-all duration-200 ease-out',
    // Focus
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'dark:focus-visible:ring-offset-gray-900',
    // Disabled
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
    // Variants and sizes
    variantStyles[variant],
    sizeStyles[size],
    className
  )

  const iconElement = icon && (
    <span className={cn(
      'transition-transform duration-200',
      iconPosition === 'right' && 'group-hover:translate-x-0.5',
      iconPosition === 'left' && 'group-hover:-translate-x-0.5'
    )}>
      {icon}
    </span>
  )

  const loadingSpinner = (
    <svg
      className="animate-spin h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )

  const content = (
    <>
      {loading && loadingSpinner}
      {!loading && iconPosition === 'left' && iconElement}
      <span>{children}</span>
      {!loading && iconPosition === 'right' && iconElement}
    </>
  )

  if (props.as === 'link') {
    const { as, to, onClick, ...rest } = props
    return (
      <Link
        to={to}
        className={cn(baseStyles, 'group')}
        onClick={onClick}
        {...rest}
      >
        {content}
      </Link>
    )
  }

  if (props.as === 'a') {
    const { as, href, target, rel, onClick, ...rest } = props
    return (
      <a
        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        href={href}
        target={target}
        rel={rel || (target === '_blank' ? 'noopener noreferrer' : undefined)}
        className={cn(baseStyles, 'group', disabled && 'pointer-events-none opacity-50')}
        onClick={onClick}
        {...rest}
      >
        {content}
      </a>
    )
  }

  const { as, type = 'button', onClick, ...rest } = props as ButtonAsButton
  return (
    <button
      ref={ref as React.ForwardedRef<HTMLButtonElement>}
      type={type}
      className={cn(baseStyles, 'group')}
      disabled={disabled || loading}
      onClick={onClick}
      {...rest}
    >
      {content}
    </button>
  )
})

Button.displayName = 'Button'

export default Button
